import { useEffect, useRef } from "react";
import {
  buildSuperProperties,
  identifyUser,
  registerSuperProperties,
  resetAnalyticsUser,
} from "../shared/analytics";
import { AuthData, useAuth } from "./Auth";
import { usePremium } from "./Premium";

/**
 * Keeps the analytics identity in sync with the auth/premium state:
 * - identifies the PostHog person (with traits) whenever the user or their
 *   premium status changes
 * - resets the anonymous id on sign out so the next visitor is not merged
 *   into the previous account
 * - registers super properties (platform, premium, role) on every event
 */
export const AnalyticsProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { authData } = useAuth() as { authData?: AuthData };
  const { isPremium, isReadOnly } = usePremium() as {
    isPremium?: boolean;
    isReadOnly?: boolean;
  };
  const previousUserId = useRef<number | undefined>(undefined);

  useEffect(() => {
    const premium = { isPremium, isReadOnly };

    if (authData?.id) {
      identifyUser(authData, premium);
      previousUserId.current = authData.id;
    } else if (previousUserId.current) {
      // Signed out: forget the person so a new login on this browser is not
      // stitched onto the previous account.
      resetAnalyticsUser();
      previousUserId.current = undefined;
    }

    registerSuperProperties(buildSuperProperties(authData, premium));
  }, [authData, isPremium, isReadOnly]);

  return <>{children}</>;
};
